import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebaar from './Sidebaar';
import { changePassword } from '../../utils/authApi';

function getStoredAuthUser() {
  try {
    const raw = localStorage.getItem('authUser');
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function splitName(user) {
  const sourceName =
    user?.name ||
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    '';

  const parts = sourceName.trim().split(/\s+/).filter(Boolean);

  return {
    firstName: user?.firstName || parts[0] || '',
    lastName: user?.lastName || parts.slice(1).join(' '),
  };
}

function validatePassword(password) {
  if (!password || password.length < 8) return 'Must contain at least 8 characters';
  if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password)) {
    return 'Must contain uppercase, lowercase letters, and numbers';
  }
  if (password.length < 12 && !/[^A-Za-z0-9]/.test(password)) {
    return 'If less than 12 characters, it must contain a special character';
  }
  return '';
}

function ValidationItem({ valid, children }) {
  return (
    <p className={`m-0 ${valid ? 'text-[#16A34A]' : 'text-[#6B7280]'}`}>
      {valid ? '✓' : '✕'} {children}
    </p>
  );
}

function EyeIcon({ hidden }) {
  if (hidden) {
    return (
      <svg
        aria-hidden="true"
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.5 12S5.75 4 12 4s9.5 8 9.5 8-3.25 8-9.5 8-9.5-8-9.5-8z"
        />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 3l18 18M10.58 10.58a2 2 0 002.84 2.84M9.88 4.24A9.77 9.77 0 0112 4c5.25 0 8.5 4.5 9.5 8a11.76 11.76 0 01-2.34 4.16M6.54 6.54C4.49 7.89 3.18 10.02 2.5 12c1 3.5 4.25 8 9.5 8 1.54 0 2.9-.39 4.08-1.03"
      />
    </svg>
  );
}

function PasswordInput({ label, value, onChange, placeholder, visible, onToggle }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[#111827] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {label}
      </span>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full rounded-full border border-[#D1D5DB] px-4 py-2.5 pr-12 text-sm text-[#111827] outline-none transition-colors focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE]"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-[#667085] transition-colors hover:bg-[#F2F4F7] hover:text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-pressed={visible}
        >
          <EyeIcon hidden={!visible} />
        </button>
      </div>
    </label>
  );
}

export default function InstSettings() {
  const navigate = useNavigate();
  const authUser = useMemo(() => getStoredAuthUser(), []);
  const initialName = splitName(authUser);

  const [firstName, setFirstName] = useState(initialName.firstName);
  const [lastName, setLastName] = useState(initialName.lastName);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const sectionHeadingStyle = {
    fontFamily: 'Manrope, sans-serif',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '0.005em',
  };

  const inputClass =
    'w-full rounded-full border border-[#D1D5DB] px-4 py-2.5 text-sm text-[#111827] outline-none transition-colors focus:border-[#2563EB] focus:ring-2 focus:ring-[#DBEAFE]';

  const hasMinLength = newPassword.length >= 8;
  const hasRequiredMix = /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) && /\d/.test(newPassword);
  const hasSpecialRule = newPassword.length >= 12 || /[^A-Za-z0-9]/.test(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSavePassword = currentPassword && hasMinLength && hasRequiredMix && hasSpecialRule && passwordsMatch && !isSavingPassword;
  const canSaveProfile = firstName.trim().length > 0;

  function handleProfileSave() {
    if (!firstName.trim()) {
      setProfileSaved(false);
      setProfileMessage('First name is required.');
      return;
    }

    const fullName = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
    const updatedUser = {
      ...(authUser || {}),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      name: fullName,
      fullName,
    };

    localStorage.setItem('authUser', JSON.stringify(updatedUser));
    window.dispatchEvent(new Event('storage'));
    setProfileSaved(true);
    setProfileMessage('Profile information saved successfully.');
  }

  async function handlePasswordSave() {
    const validationError = validatePassword(newPassword);

    if (!currentPassword) {
      setPasswordSaved(false);
      setPasswordMessage('Current password is required.');
      return;
    }

    if (validationError) {
      setPasswordSaved(false);
      setPasswordMessage(validationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordSaved(false);
      setPasswordMessage('Both passwords must match.');
      return;
    }

    setIsSavingPassword(true);

    try {
      const data = await changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
      });

      setPasswordSaved(true);
      setPasswordMessage(data.message || 'Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordSaved(false);
      setPasswordMessage(error.message || 'Unable to update password.');
    } finally {
      setIsSavingPassword(false);
    }
  }

  function handleLogout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    navigate('/');
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex flex-col lg:flex-row">
      <Sidebaar />

      <main className="flex-1 px-4 sm:px-8 lg:px-10 py-6 sm:py-8">
        <h1
          className="text-[32px] font-semibold text-[#111827] m-0 mb-6"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Settings
        </h1>

        <div className="space-y-4">
          {/* Personal Information */}
          <section className="grid grid-cols-1 lg:grid-cols-2 bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            <div className="p-7 border-r border-[#E5E7EB]">
              <h2 className="m-0 text-[#111827]" style={sectionHeadingStyle}>
                Personal Information
              </h2>
              <p className="mt-3 text-base text-[#6B7280]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                The information associated with your account.
              </p>
            </div>

            <div className="p-7 space-y-4">
              <label className="block">
                <span className="block text-sm font-semibold text-[#111827] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  First name
                </span>
                <input
                  type="text"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    setProfileMessage('');
                  }}
                  placeholder="First name"
                  className={inputClass}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                />
              </label>

              <label className="block">
                <span className="block text-sm font-semibold text-[#111827] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Last name <span className="text-[#9CA3AF] font-normal">(optional)</span>
                </span>
                <input
                  type="text"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    setProfileMessage('');
                  }}
                  placeholder="Last name"
                  className={inputClass}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                />
              </label>

              {profileMessage ? (
                <div className={`rounded-xl px-4 py-3 text-sm ${profileSaved ? 'bg-[#F0FDF4] text-[#166534]' : 'bg-[#FEF2F2] text-[#B91C1C]'}`}>
                  {profileMessage}
                </div>
              ) : null}

              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handleProfileSave}
                  disabled={!canSaveProfile}
                  className={`rounded-full px-6 py-2.5 text-sm font-medium transition-colors ${
                    canSaveProfile ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' : 'bg-[#EEF2F7] text-[#C0C7D1]'
                  }`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </section>

          {/* Change Password */}
          <section className="grid grid-cols-1 lg:grid-cols-2 bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            <div className="p-7 border-r border-[#E5E7EB]">
              <h2 className="m-0 text-[#111827]" style={sectionHeadingStyle}>
                Change Password
              </h2>
              <p className="mt-3 text-base text-[#6B7280]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                We recommend that you periodically update your password to help prevent unauthorized access to your account.
              </p>
            </div>

            <div className="p-7 space-y-4">
              <PasswordInput
                label="Current Password"
                value={currentPassword}
                onChange={(event) => {
                  setCurrentPassword(event.target.value);
                  setPasswordMessage('');
                }}
                placeholder="Your Current Password"
                visible={showCurrentPassword}
                onToggle={() => setShowCurrentPassword((current) => !current)}
              />

              <PasswordInput
                label="New Password"
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setPasswordMessage('');
                }}
                placeholder="Your New Password"
                visible={showNewPassword}
                onToggle={() => setShowNewPassword((current) => !current)}
              />

              <PasswordInput
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setPasswordMessage('');
                }}
                placeholder="Confirm Your New Password"
                visible={showConfirmPassword}
                onToggle={() => setShowConfirmPassword((current) => !current)}
              />

              <div className="text-sm space-y-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <ValidationItem valid={hasMinLength}>Must contain at least 8 characters</ValidationItem>
                <ValidationItem valid={hasRequiredMix}>Must contain uppercase, lowercase letters, and numbers</ValidationItem>
                <ValidationItem valid={hasSpecialRule}>If less than 12 characters, must contain a special character</ValidationItem>
                <ValidationItem valid={passwordsMatch}>Both passwords must match</ValidationItem>
              </div>

              {passwordMessage ? (
                <div className={`rounded-xl px-4 py-3 text-sm ${passwordSaved ? 'bg-[#F0FDF4] text-[#166534]' : 'bg-[#FEF2F2] text-[#B91C1C]'}`}>
                  {passwordMessage}
                </div>
              ) : null}

              <div className="pt-2 flex justify-end">
                <button
                  type="button"
                  onClick={handlePasswordSave}
                  disabled={!canSavePassword}
                  className={`rounded-full px-6 py-2.5 text-sm font-medium transition-colors ${
                    canSavePassword ? 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]' : 'bg-[#EEF2F7] text-[#C0C7D1]'
                  }`}
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {isSavingPassword ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </section>

          {/* Logout */}
          <section className="grid grid-cols-1 lg:grid-cols-2 bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            <div className="p-7 border-r border-[#E5E7EB] flex items-center">
              <h2 className="m-0 text-[#111827]" style={sectionHeadingStyle}>
                Logout
              </h2>
            </div>
            <div className="p-7 flex items-center">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full rounded-full bg-[#E24A6A] text-white py-2.5 text-base font-medium hover:bg-[#D63B5D] transition-colors"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Logout
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
